import { CommunityAssessmentData } from '@/types/entities/clients/assessment';

export interface DiscoveryCallSlot {
  id: string;
  date: string;
  time: string;
  duration: number; // minutes
  available: boolean;
  timeZone: string;
}

export interface DiscoveryCallBooking {
  id: string;
  assessmentId: string;
  communityName: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  selectedSlot: DiscoveryCallSlot;
  tier: 'silver' | 'gold' | 'custom';
  agenda: DiscoveryCallAgenda;
  specialRequirements?: string;
  stakeholders: Array<{
    name: string;
    email?: string;
    role: string;
  }>;
  status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled';
  createdAt: string;
  confirmationSent: boolean;
}

export interface DiscoveryCallAgenda {
  duration: number;
  sections: Array<{
    title: string;
    duration: number;
    topics: string[];
    customizations: string[];
  }>;
  roiProjections: {
    potentialSavings: number;
    treasuryGrowth: number;
    efficiencyGains: string[];
  };
  demoFeatures: string[];
  discussionPoints: string[];
}

export interface CalendarIntegration {
  provider: 'calendly' | 'google' | 'outlook' | 'custom';
  bookingUrl?: string;
  apiKey?: string;
  calendarId?: string;
}

class DiscoveryCallService {
  private bookings: DiscoveryCallBooking[] = [];
  private availableSlots: DiscoveryCallSlot[] = [];

  constructor() {
    this.initializeAvailableSlots();
  }

  private initializeAvailableSlots(): void {
    // Generate available time slots for the next 30 days
    const slots: DiscoveryCallSlot[] = [];
    const startDate = new Date();

    for (let i = 1; i <= 30; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);

      // Skip weekends
      if (date.getDay() === 0 || date.getDay() === 6) continue;

      // Generate time slots: 9 AM - 5 PM in 1-hour intervals
      for (let hour = 9; hour <= 17; hour++) {
        if (hour === 12) continue; // Skip lunch hour

        slots.push({
          id: `${date.toISOString().split('T')[0]}-${hour}:00`,
          date: date.toISOString().split('T')[0],
          time: `${hour.toString().padStart(2, '0')}:00`,
          duration: 60,
          available: true,
          timeZone: 'America/New_York', // Default timezone
        });
      }
    }

    this.availableSlots = slots;
  }

  generatePersonalizedAgenda(
    assessmentData: Partial<CommunityAssessmentData>,
  ): DiscoveryCallAgenda {
    const tier = assessmentData.selectedTier || 'silver';
    const treasuryAmount = assessmentData.financialPosition?.treasuryAmount || 0;
    const totalUnits = parseInt(assessmentData.totalUnits || '0');
    const challenges = assessmentData.biggestChallenges || [];
    const goals = assessmentData.primaryGoals || [];

    // Calculate ROI projections
    const monthlyFees = assessmentData.financialPosition?.monthlyHOAFees || 0;
    const annualRevenue = monthlyFees * totalUnits * 12;
    const potentialSavings = Math.min(annualRevenue * 0.15, 50000); // 15% savings cap
    const treasuryGrowth = treasuryAmount * 0.05; // 5% investment return

    // Determine agenda duration based on tier
    const baseDuration = tier === 'custom' ? 90 : tier === 'gold' ? 60 : 45;

    const agenda: DiscoveryCallAgenda = {
      duration: baseDuration,
      sections: [
        {
          title: 'Welcome & Community Overview',
          duration: 10,
          topics: [
            `Review ${assessmentData.communityName || 'your community'} profile`,
            `Understand your ${totalUnits} units and governance structure`,
            'Confirm assessment responses and priorities',
          ],
          customizations: [],
        },
        {
          title: 'Challenge-Focused Solutions',
          duration: tier === 'custom' ? 25 : 20,
          topics: challenges.slice(0, 3).map((challenge) => `Address: ${challenge}`),
          customizations: this.getChallengeSolutions(challenges),
        },
        {
          title: 'Financial Impact & ROI',
          duration: 15,
          topics: [
            `Review $${potentialSavings.toLocaleString()} potential annual savings`,
            `Discuss $${treasuryAmount.toLocaleString()} treasury optimization`,
            'Revenue generation opportunities',
          ],
          customizations: [`Treasury size: $${treasuryAmount.toLocaleString()}`],
        },
        {
          title: 'Live Platform Demonstration',
          duration: tier === 'custom' ? 30 : 20,
          topics: goals.slice(0, 3).map((goal) => `Demo: ${goal}`),
          customizations: this.getDemoFeatures(tier, goals, challenges),
        },
        {
          title: 'Implementation Planning',
          duration: tier === 'custom' ? 15 : 10,
          topics: [
            'Timeline discussion',
            'Stakeholder engagement strategy',
            'Next steps and proposal delivery',
          ],
          customizations: [],
        },
      ],
      roiProjections: {
        potentialSavings,
        treasuryGrowth,
        efficiencyGains: this.getEfficiencyGains(goals, challenges),
      },
      demoFeatures: this.getDemoFeatures(tier, goals, challenges),
      discussionPoints: this.getDiscussionPoints(assessmentData),
    };

    return agenda;
  }

  private getChallengeSolutions(challenges: string[]): string[] {
    const solutionMap: { [key: string]: string } = {
      voting: 'Cryptographic voting system demonstration',
      communication: 'Multi-channel communication platform',
      financial: 'Real-time financial dashboards and reporting',
      maintenance: 'Automated work order and vendor management',
      transparency: 'Open-book financial transparency tools',
    };

    return challenges
      .map((challenge) => solutionMap[challenge] || `Custom solution for ${challenge}`)
      .filter(Boolean);
  }

  private getDemoFeatures(tier: string, goals: string[], challenges: string[]): string[] {
    const baseFeatures = [
      'Community dashboard overview',
      'Financial transparency tools',
      'Communication system',
    ];

    const goldFeatures = [
      'Advanced voting workflows',
      'Vendor management system',
      'Financial analytics',
    ];

    const customFeatures = [
      'White-label customization',
      'API integrations',
      'Custom reporting suite',
    ];

    const features = [...baseFeatures];
    if (tier === 'gold' || tier === 'custom') {
      features.push(...goldFeatures);
    }
    if (tier === 'custom') {
      features.push(...customFeatures);
    }

    return features;
  }

  private getEfficiencyGains(goals: string[], challenges: string[]): string[] {
    const gains = [];

    if (goals.includes('Reduce administrative burden')) {
      gains.push('75% reduction in administrative tasks');
    }
    if (goals.includes('Improve decision-making speed')) {
      gains.push('50% faster decision cycles');
    }
    if (challenges.includes('communication')) {
      gains.push('90% improvement in resident engagement');
    }
    if (challenges.includes('financial')) {
      gains.push('Real-time financial visibility');
    }

    return gains.length > 0 ? gains : ['Streamlined operations', 'Improved resident satisfaction'];
  }

  private getDiscussionPoints(assessmentData: Partial<CommunityAssessmentData>): string[] {
    const points = [];

    if (assessmentData.implementationReadiness?.biggestConcerns) {
      points.push(`Address concern: ${assessmentData.implementationReadiness.biggestConcerns}`);
    }

    if (assessmentData.keyStakeholders && assessmentData.keyStakeholders.length > 1) {
      points.push('Multi-stakeholder presentation strategy');
    }

    if (assessmentData.implementationReadiness?.timeline === 'immediate') {
      points.push('Expedited implementation options');
    }

    points.push('Board presentation materials');
    points.push('Pilot program options');

    return points;
  }

  getAvailableSlots(timeZone: string = 'America/New_York'): DiscoveryCallSlot[] {
    // Filter out already booked slots
    const bookedSlotIds = this.bookings
      .filter((booking) => booking.status !== 'cancelled')
      .map((booking) => booking.selectedSlot.id);

    return this.availableSlots
      .filter((slot) => slot.available && !bookedSlotIds.includes(slot.id))
      .slice(0, 20); // Return next 20 available slots
  }

  async scheduleDiscoveryCall(
    assessmentData: Partial<CommunityAssessmentData>,
    selectedSlotId: string,
    stakeholders: Array<{ name: string; email?: string; role: string }> = [],
  ): Promise<DiscoveryCallBooking> {
    const selectedSlot = this.availableSlots.find((slot) => slot.id === selectedSlotId);
    if (!selectedSlot || !selectedSlot.available) {
      throw new Error('Selected time slot is not available');
    }

    const agenda = this.generatePersonalizedAgenda(assessmentData);

    const booking: DiscoveryCallBooking = {
      id: `booking-${Date.now()}`,
      assessmentId: `assessment-${Date.now()}`,
      communityName: assessmentData.communityName || 'Community',
      contactName: assessmentData.contactName || 'Contact',
      contactEmail: assessmentData.contactEmail || '',
      contactPhone: assessmentData.contactPhone,
      selectedSlot,
      tier: assessmentData.selectedTier || 'silver',
      agenda,
      specialRequirements: assessmentData.specialRequirements,
      stakeholders,
      status: 'scheduled',
      createdAt: new Date().toISOString(),
      confirmationSent: false,
    };

    this.bookings.push(booking);

    // Mark slot as unavailable
    selectedSlot.available = false;

    // Send confirmation (mock)
    await this.sendConfirmationEmail(booking);
    booking.confirmationSent = true;

    return booking;
  }

  private async sendConfirmationEmail(booking: DiscoveryCallBooking): Promise<void> {
    // Mock email service - in production, this would integrate with actual email service
    console.log(`Sending confirmation email to ${booking.contactEmail}`);
    console.log(
      `Discovery Call scheduled for ${booking.selectedSlot.date} at ${booking.selectedSlot.time}`,
    );

    // Would include:
    // - Calendar invite (.ics file)
    // - Agenda overview
    // - Preparation materials
    // - Meeting link (Zoom/Teams)
  }

  async generateCalendarInvite(booking: DiscoveryCallBooking): Promise<string> {
    // Generate .ics calendar file content
    const startDate = new Date(`${booking.selectedSlot.date}T${booking.selectedSlot.time}:00`);
    const endDate = new Date(startDate.getTime() + booking.agenda.duration * 60000);

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Community App//Discovery Call//EN
BEGIN:VEVENT
UID:${booking.id}@example.com
DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z
DTSTART:${startDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z
DTEND:${endDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z
SUMMARY:Discovery Call - ${booking.communityName}
DESCRIPTION:Personalized demonstration and consultation for ${booking.communityName}
LOCATION:Video Conference (link to be provided)
BEGIN:VALARM
TRIGGER:-PT15M
ACTION:DISPLAY
DESCRIPTION:Discovery Call reminder
END:VALARM
END:VEVENT
END:VCALENDAR`;

    return icsContent;
  }

  getBooking(bookingId: string): DiscoveryCallBooking | null {
    return this.bookings.find((booking) => booking.id === bookingId) || null;
  }

  updateBookingStatus(bookingId: string, status: DiscoveryCallBooking['status']): boolean {
    const booking = this.getBooking(bookingId);
    if (booking) {
      booking.status = status;
      return true;
    }
    return false;
  }

  getUpcomingCalls(): DiscoveryCallBooking[] {
    return this.bookings
      .filter((booking) => booking.status === 'scheduled' || booking.status === 'confirmed')
      .sort(
        (a, b) => new Date(a.selectedSlot.date).getTime() - new Date(b.selectedSlot.date).getTime(),
      );
  }
}

export const discoveryCallService = new DiscoveryCallService();
